/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC, useState } from 'react';

import { startOIDCFlow, type OIDCProvider } from './service';
import { OidcSection } from './oidc-section';
import { useLoginI18n, type LoginStrings } from './i18n';
import { CredentialForm } from './credential-form';

interface FormPanelProps {
  email: string;
  password: string;
  loginLoading: boolean;
  registerLoading: boolean;
  providers: OIDCProvider[];
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onLogin: () => void;
  onRegister: () => void;
}

const SignupLink: FC<{
  t: LoginStrings;
  disabled: boolean;
  onClick: () => void;
}> = ({ t, disabled, onClick }) => (
  <div
    style={{
      marginLeft: 'auto',
      fontSize: 13,
      color: 'var(--text-3)',
    }}
  >
    {t.signup_prompt}{' '}
    <a
      data-testid="login.button.signup"
      onClick={() => {
        if (!disabled) {
          onClick();
        }
      }}
      style={{
        color: 'var(--brand-600)',
        fontWeight: 500,
        cursor: 'pointer',
      }}
    >
      {t.signup_link}
    </a>
  </div>
);

const InlineWordmark: FC = () => (
  <div
    className="trebox-login-root__inline-wordmark"
    style={{
      display: 'none',
      alignItems: 'center',
      gap: 10,
      marginBottom: 24,
    }}
  >
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: 'linear-gradient(135deg, #0064FA 0%, #4D8FFB 100%)',
        color: 'white',
        fontWeight: 700,
        fontSize: 18,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 2px rgba(0,100,250,.25)',
      }}
    >
      T
    </div>
    <span
      style={{
        fontSize: 16,
        fontWeight: 600,
        letterSpacing: -0.2,
        color: 'var(--text-1)',
      }}
    >
      Trebox
    </span>
  </div>
);

const FormHeading: FC<{ t: LoginStrings }> = ({ t }) => (
  <>
    <InlineWordmark />
    <h2
      style={{
        margin: 0,
        fontSize: 26,
        fontWeight: 600,
        letterSpacing: -0.4,
        color: 'var(--text-1)',
      }}
    >
      {t.title}
    </h2>
    <p
      style={{
        margin: '8px 0 32px',
        color: 'var(--text-3)',
        fontSize: 14,
      }}
    >
      {t.subtitle}
    </p>
  </>
);

// Render `登录即表示同意 {{tos}} 与 {{privacy}}。 ...` template into JSX.
const TermsFootnote: FC<{ t: LoginStrings }> = ({ t }) => {
  const parts = t.tos_full.split(/(\{\{tos\}\}|\{\{privacy\}\})/g);
  return (
    <p
      style={{
        marginTop: 24,
        fontSize: 11,
        color: 'var(--text-4)',
        lineHeight: 1.6,
      }}
    >
      {parts.map((part, idx) => {
        if (part === '{{tos}}') {
          return (
            <a
              key={idx}
              data-testid="login.link.terms"
              href="https://github.com/catinboxai/trebox?tab=Apache-2.0-1-ov-file"
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--text-2)' }}
            >
              {t.tos_link}
            </a>
          );
        }
        if (part === '{{privacy}}') {
          return (
            <a key={idx} style={{ color: 'var(--text-2)' }}>
              {t.privacy_link}
            </a>
          );
        }
        return <span key={idx}>{part}</span>;
      })}
    </p>
  );
};

export const FormPanel: FC<FormPanelProps> = ({
  email,
  password,
  loginLoading,
  registerLoading,
  providers,
  onEmailChange,
  onPasswordChange,
  onLogin,
  onRegister,
}) => {
  const t = useLoginI18n();
  const [remember, setRemember] = useState(true);
  const oidcRedirectURI = `${window.location.origin}/oauth-callback`;

  return (
    <main
      className="trebox-login-root__form-main"
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '40px clamp(32px, 5vw, 64px)',
        overflowY: 'auto',
        minWidth: 0,
        background: 'var(--bg-form)',
      }}
    >
      <SignupLink t={t} disabled={registerLoading} onClick={onRegister} />

      <div style={{ margin: 'auto', width: '100%', maxWidth: 380 }}>
        <FormHeading t={t} />
        <CredentialForm
          email={email}
          password={password}
          remember={remember}
          loginLoading={loginLoading}
          onEmailChange={onEmailChange}
          onPasswordChange={onPasswordChange}
          onRememberChange={setRemember}
          onLogin={onLogin}
        />
        <OidcSection
          providers={providers}
          dividerLabel={t.oidc_divider}
          onSelect={p => startOIDCFlow(p.id, oidcRedirectURI)}
        />
        <TermsFootnote t={t} />
      </div>
    </main>
  );
};
